/**
 * Client API cho màn hình "Lịch sử tạo báo cáo" (BLD Reports).
 *
 * Server trả camelCase cho cả response lẫn request body:
 * - GET  /api/bld-reports/history?periodType&status&from&to&page&limit
 * - POST /api/bld-reports/test-generate ← { periodType, periodEnd, sources,
 *                                          persist, sendTeams, includeSpecialIncidents }
 * - GET  /api/bld-reports/:id/html  (HTML document)
 * - GET  /api/bld-reports/:id/png   (PNG hoặc 404)
 *
 * Không lưu secret ở client; chỉ gọi API qua axios dùng chung (đã inject
 * x-username / x-user-id).
 *
 * Mọi hàm nhận `apiBaseUrl` (API base runtime — prop `apiBaseUrl` khi S3 Explorer được
 * nhúng vào frontend host) và ghép URL tuyệt đối, KHÔNG dựa vào `axios.defaults.baseURL`:
 * bundle đã build của package không biết domain triển khai nên mặc định rơi về
 * `http://localhost:3000` (xem `lib/config.ts`).
 */

import axios from './axios';
import { type BldSourceKey } from './bld-reports-api';

/** Múi giờ hiển thị của toàn bộ báo cáo BLD. */
export const BLD_HISTORY_TIMEZONE = 'Asia/Ho_Chi_Minh';

export type BldPeriodType = 'day' | 'week' | 'month' | 'quarter';

export type BldReportRunStatus = 'running' | 'success' | 'failed' | 'skipped';

export type BldReportRunTrigger = 'scheduler' | 'manual' | 'test';

/** Một bản ghi trong lịch sử tạo báo cáo. */
export interface BldReportHistoryItem {
  runId: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  periodType: BldPeriodType;
  periodKey: string;
  status: BldReportRunStatus;
  errorMessage: string | null;
  teamsSent: boolean;
  teamsChannels: unknown;
  teamsSentAt: string | null;
  reportId: string | null;
  trigger: BldReportRunTrigger;
  dryRun: boolean;
  htmlUrl: string | null;
  pngUrl: string | null;
}

/** Tham số lọc cho GET /api/bld-reports/history. */
export interface BldReportHistoryQuery {
  periodType?: BldPeriodType | null;
  status?: BldReportRunStatus | null;
  from?: string | null;
  to?: string | null;
  page?: number;
  limit?: number;
}

export interface BldReportHistoryResponse {
  data: BldReportHistoryItem[];
  total: number;
  page: number;
  limit: number;
}

/** Thống kê đọc dữ liệu của 1 nguồn trong lần chạy thử. */
export interface BldTestGenerateSourceStats {
  rows: number;
  skippedSheets: string[];
  missingLabels: string[];
  warnings: string[];
}

/** Ngày dữ liệu thực tế của 1 nguồn (có thể đã backfill về ngày trước). */
export interface BldTestGenerateSourceDate {
  requestedDate: string;
  dataDate: string;
  backfilled: boolean;
}

export interface BldTestGenerateResult {
  ok: boolean;
  periodType: BldPeriodType;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  sourceDates: Record<string, BldTestGenerateSourceDate>;
  stats: Record<string, BldTestGenerateSourceStats>;
  htmlPreviewUrl: string | null;
  html: string | null;
  pngUrl: string | null;
  warnings: string[];
  errors: string[];
  durationMs: number;
}

/** Body POST /api/bld-reports/test-generate. */
export interface BldTestGenerateInput {
  periodType?: BldPeriodType;
  /** Ngày cuối của kỳ đã chọn (YYYY-MM-DD); server tự suy ra ngày chạy tương ứng. */
  periodEnd?: string;
  sources?: BldSourceKey[];
  persist?: boolean;
  sendTeams?: boolean;
  includeSpecialIncidents?: boolean;
}

export const BLD_PERIOD_TYPE_LABELS: Record<BldPeriodType, string> = {
  day: 'Ngày',
  week: 'Tuần',
  month: 'Tháng',
  quarter: 'Quý',
};

export const BLD_PERIOD_TYPE_OPTIONS: readonly BldPeriodType[] = [
  'day',
  'week',
  'month',
  'quarter',
];

export const BLD_RUN_STATUS_LABELS: Record<BldReportRunStatus, string> = {
  running: 'Đang chạy',
  success: 'Thành công',
  failed: 'Thất bại',
  skipped: 'Bỏ qua',
};

export const BLD_RUN_STATUS_OPTIONS: readonly BldReportRunStatus[] = [
  'success',
  'failed',
  'running',
  'skipped',
];

export const BLD_RUN_TRIGGER_LABELS: Record<BldReportRunTrigger, string> = {
  scheduler: 'Lịch tự động',
  manual: 'Chạy tay',
  test: 'Chạy test',
};

/** Class Tailwind cho badge trạng thái (success=xanh, failed=đỏ, running=vàng, skipped=xám). */
export const BLD_RUN_STATUS_CLASSES: Record<BldReportRunStatus, string> = {
  success: 'border-transparent bg-green-600 text-white',
  failed: 'border-transparent bg-red-600 text-white',
  running: 'border-transparent bg-amber-500 text-white',
  skipped: 'border-transparent bg-gray-400 text-white',
};

/** Định dạng thời lượng: 850 → "850ms", 1250 → "1.2s", 65000 → "1m 5s". */
export function formatBldDuration(durationMs: number | null | undefined): string | null {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs < 0) {
    return null;
  }
  if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
  const seconds = durationMs / 1000;
  if (seconds < 60) {
    // Cắt bớt (không làm tròn) để "1250ms" hiển thị "1.2s" như spec.
    const truncated = Math.floor(seconds * 10) / 10;
    return `${Number.isInteger(truncated) ? truncated.toFixed(0) : truncated.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const restSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${restSeconds}s`;
}

/** Định dạng thời điểm theo giờ Việt Nam; trả null nếu giá trị rỗng/không hợp lệ. */
export function formatBldDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('vi-VN', {
    timeZone: BLD_HISTORY_TIMEZONE,
    hour12: false,
  });
}

/** Nhãn kỳ báo cáo: "Ngày 2026-09-25", "Tuần 2026-W39", ... */
export function formatBldPeriodLabel(
  periodType: BldPeriodType,
  periodKey: string,
): string {
  const label = BLD_PERIOD_TYPE_LABELS[periodType];
  const key = periodKey?.trim();
  if (!label) return key ?? '';
  return key ? `${label} ${key}` : label;
}

/**
 * Ghép đường dẫn API tương đối (vd `/api/bld-reports/:id/html`) với API base runtime
 * (`apiBaseUrl` của caller) trước khi mở tab mới. `window.open` không đi qua axios nên
 * đường dẫn tương đối sẽ mở nhầm trên origin của SPA (ra trang Not Found).
 * URL tuyệt đối (http/https) giữ nguyên.
 */
export function resolveBldApiUrl(apiBaseUrl: string, url: string): string {
  if (/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url)) return url;
  const base = apiBaseUrl.replace(/\/+$/, '');
  if (!base) return url;
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

interface BldErrorPayload {
  error?: string;
  message?: string;
  issues?: { message?: string }[];
}

/**
 * Lấy thông điệp lỗi từ response của server: ưu tiên issues[0].message,
 * sau đó `error`, rồi `message`.
 */
export function extractBldError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const data = err.response.data as BldErrorPayload;
    const firstIssue = Array.isArray(data.issues)
      ? data.issues.find(
          (issue) => issue && typeof issue.message === 'string' && issue.message.trim().length > 0,
        )
      : undefined;
    if (firstIssue?.message) {
      return data.error ? `${data.error}: ${firstIssue.message}` : firstIssue.message;
    }
    if (data.error) return data.error;
    if (data.message) return data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/** GET /api/bld-reports/history — danh sách bản ghi đã phân trang. */
export async function listBldReportHistory(
  apiBaseUrl: string,
  query: BldReportHistoryQuery = {},
): Promise<BldReportHistoryResponse> {
  const page = typeof query.page === 'number' && query.page > 0 ? query.page : 1;
  const limit = typeof query.limit === 'number' && query.limit > 0 ? query.limit : 20;
  const params: {
    page: number;
    limit: number;
    periodType?: BldPeriodType;
    status?: BldReportRunStatus;
    from?: string;
    to?: string;
  } = { page, limit };
  if (query.periodType) params.periodType = query.periodType;
  if (query.status) params.status = query.status;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;

  try {
    const response = await axios.get<Partial<BldReportHistoryResponse>>(
      `${apiBaseUrl}/api/bld-reports/history`,
      { params },
    );
    const data = response.data ?? {};
    return {
      data: Array.isArray(data.data) ? data.data : [],
      total: toNumber(data.total, 0),
      page: toNumber(data.page, page),
      limit: toNumber(data.limit, limit),
    };
  } catch (err) {
    throw new Error(extractBldError(err, 'Không tải được lịch sử tạo báo cáo.'));
  }
}

function normalizeStats(value: unknown): Record<string, BldTestGenerateSourceStats> {
  if (!value || typeof value !== 'object') return {};
  const result: Record<string, BldTestGenerateSourceStats> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const item = (raw ?? {}) as Partial<BldTestGenerateSourceStats>;
    result[key] = {
      rows: toNumber(item.rows, 0),
      skippedSheets: toStringArray(item.skippedSheets),
      missingLabels: toStringArray(item.missingLabels),
      warnings: toStringArray(item.warnings),
    };
  }
  return result;
}

function normalizeSourceDates(value: unknown): Record<string, BldTestGenerateSourceDate> {
  if (!value || typeof value !== 'object') return {};
  const result: Record<string, BldTestGenerateSourceDate> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const item = (raw ?? {}) as Partial<BldTestGenerateSourceDate>;
    result[key] = {
      requestedDate: typeof item.requestedDate === 'string' ? item.requestedDate : '',
      dataDate: typeof item.dataDate === 'string' ? item.dataDate : '',
      backfilled: item.backfilled === true,
    };
  }
  return result;
}

/** POST /api/bld-reports/test-generate — chạy thử pipeline (mặc định không persist). */
export async function runBldTestGenerate(
  apiBaseUrl: string,
  input: BldTestGenerateInput = {},
): Promise<BldTestGenerateResult> {
  const body: BldTestGenerateInput = {
    persist: input.persist ?? false,
  };
  if (input.periodType) body.periodType = input.periodType;
  if (input.periodEnd) body.periodEnd = input.periodEnd;
  if (input.sources && input.sources.length > 0) body.sources = [...input.sources];
  if (typeof input.sendTeams === 'boolean') body.sendTeams = input.sendTeams;
  if (typeof input.includeSpecialIncidents === 'boolean') {
    body.includeSpecialIncidents = input.includeSpecialIncidents;
  }

  try {
    const response = await axios.post<Partial<BldTestGenerateResult>>(
      `${apiBaseUrl}/api/bld-reports/test-generate`,
      body,
    );
    const data = response.data ?? {};
    return {
      ok: data.ok === true,
      periodType: (data.periodType ?? 'day') as BldPeriodType,
      periodKey: typeof data.periodKey === 'string' ? data.periodKey : '',
      periodStart: typeof data.periodStart === 'string' ? data.periodStart : '',
      periodEnd: typeof data.periodEnd === 'string' ? data.periodEnd : '',
      sourceDates: normalizeSourceDates(data.sourceDates),
      stats: normalizeStats(data.stats),
      htmlPreviewUrl: typeof data.htmlPreviewUrl === 'string' ? data.htmlPreviewUrl : null,
      html: typeof data.html === 'string' ? data.html : null,
      pngUrl: typeof data.pngUrl === 'string' ? data.pngUrl : null,
      warnings: toStringArray(data.warnings),
      errors: toStringArray(data.errors),
      durationMs: toNumber(data.durationMs, 0),
    };
  } catch (err) {
    throw new Error(extractBldError(err, 'Không chạy được test tạo báo cáo.'));
  }
}
